import { ImagePlus, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { StrategySlideMedia } from '@/types/strategy.type';
import { KitIconButton } from '@/app/routes/dashboard/components/kit/IconButton';

/** Still image for a saved slot: the image itself, or a video's poster. */
export function slideMediaStill(media?: StrategySlideMedia): string | null | undefined {
  return media?.kind === 'image' ? media.url : media?.kind === 'reference' ? media.imageUrl ?? media.thumbnailUrl : media?.thumbnailUrl;
}

/** Editing hooks for one slide-media slot; absent for anyone who can't set media. */
export interface MediaSlotControls { label: string; disabled: boolean; onChoose: () => void; onRemove?: () => void }

/** Change / remove icons, shown while hovering or focusing the filled slot. */
export function MediaSlotHoverActions({ controls }: { controls: MediaSlotControls }) {
  return <div className="strategy-media-hover-actions">
    <KitIconButton kind="media" size="md" icon={Pencil} aria-label={`Change ${controls.label}`} title="Change media" disabled={controls.disabled} onClick={controls.onChoose} />
    {controls.onRemove && <KitIconButton kind="media" size="md" icon={Trash2} aria-label={`Remove ${controls.label}`} title="Remove media" disabled={controls.disabled} onClick={controls.onRemove} />}
  </div>;
}

/**
 * One slide picture slot. A viewer who can't set media never sees it empty;
 * the team sees an empty well with "Choose a picture" (binding instructions §5).
 */
export function SlideMediaWell({ label, media, controls }: { label: string; media?: StrategySlideMedia; controls?: MediaSlotControls }) {
  const [failed, setFailed] = useState(false);
  const src = slideMediaStill(media);
  const video = media && media.kind !== 'image' ? media.videoUrl : null;
  const filled = (!!src || !!video) && !failed;
  if (!filled && !controls) return null;
  const empty = <><ImagePlus aria-hidden size={24} /><span>{failed ? 'Media couldn’t load · choose it again' : 'Choose a picture'}</span></>;
  return <figure className={`strategy-research-media${filled ? ' has-media' : ''}`}>
    {!filled ? <button type="button" className="strategy-research-media-empty" aria-label={`Choose a picture: ${label}`} disabled={controls!.disabled} onClick={controls!.onChoose}>{empty}</button>
      : video ? <video src={video} poster={src ?? undefined} controls playsInline preload="metadata" onError={() => setFailed(true)} aria-label={label} />
        : <img src={src!} alt={label} onError={() => setFailed(true)} />}
    {filled && media && media.kind !== 'image' && <figcaption>{media.title}</figcaption>}
    {controls && (filled || failed) && <MediaSlotHoverActions controls={controls} />}
  </figure>;
}
